/**
 * The paperdoll (2026-09-02).
 *
 * User: "I want something like a character paperdoll/silhouette, where I equip
 * a helmet on the head, a sword and shield in the hands, and so on."
 *
 * ## All six slots are live (2026-09-04)
 *
 * They were not. This file used to say Tallow had no gear system and that
 * four of the six slots were drawn only so the doll would not look complete
 * at two — an honest gap, but a gap. `equipment.md`'s worn-gear amendment
 * closed it: head, chest, back and trinket each take a real piece with real
 * numbers, found in the dungeon and tiered by how deep you went for it.
 *
 * The old note also claimed the art pack ships no head or chest pieces. That
 * was half wrong and worth recording: every class model carries its own
 * wearable sub-meshes (`Knight_Helmet`, `Knight_Cape`, `Barbarian_BearHat`,
 * `Druid_Backpack`) and the rig has `head`, `chest` and `spine` bones to
 * mount to. Showing worn gear *on the delver* is still to do; showing it in
 * the inventory, which is what was asked for, is done.
 *
 * ## The centre is your delver, not a silhouette
 *
 * A generic silhouette would be the fifth art style on this screen, so the
 * doll wears the face of the character it is describing.
 *
 * It used to wear `ui/lobby/portrait-<class>.png`, and that was the wrong
 * picture (2026-09-06). User: *"Make it more visually polished and
 * user-friendly, so that items and the hero are not cut off."* Those files are
 * 152x152 head crops whose chin is cut by the asset itself, drawn into a
 * 111x213 niche — upscaled past native, beheaded, and still leaving a third of
 * the plate empty. A paperdoll is a figure you hang gear on; that was a
 * floating head.
 *
 * `ui/paperdoll/<class>.webp` is a full-body render of the same `.glb` the
 * delver is built from, posed out of its shipped T-pose by
 * `tools/render-class-portrait.py`. Same rule the loot and worn icons keep:
 * the picture is a render of the thing itself.
 *
 * Pure string builders, same as the rest of `ui/`.
 */

import type { EquippedArms, EquipmentCatalogRow, EquipmentSlot } from '../net/equipment-api';
import type { LoadoutRow } from '../net/loadout-api';
import type { CharacterClassId } from '../config/character-roster';
import type { GearCatalogRow, GearBonuses } from '../net/gear-api';
import type { GearSlot } from '../config/gear-defs';
import { GEAR_SLOT_LABEL } from '../config/gear-defs';
import { amountMarkup } from './currency';
import { gearIconSrc, consumableIconSrc, esc, ICON_FALLBACK_ATTR } from './item-icon';
import { NC_, t, tc, tp, formatNumber } from '../i18n/i18n';

/** A slot the doll draws, in reading order down each side. */
export interface DollSlot {
  key: string;
  /** Drawn with `tc('gear slot', label)`. */
  label: string;
  /** An arms slot — the two hands, resolved through `equipment-api.ts`. */
  slot?: EquipmentSlot;
  /** A worn-gear slot — the four the ladder filled, resolved through `gear-api.ts`. */
  gearSlot?: GearSlot;
}

/** Where a worn piece's icon lives — rendered from the pack's own wearable sub-meshes. */
export function wornIconSrc(gearId: string): string {
  return `/assets/ui/worn/${gearId}.webp`;
}

/** Tier is depth, and depth is the only difficulty number this game has — so it carries the frame. */
export function tierFrameClass(tier: number): string {
  if (tier >= 3) return 'border-gold/70';
  if (tier === 2) return 'border-[#8fa2b8]/60';
  return 'border-border';
}

/**
 * The doll's three groups, and why they are three (2026-09-05).
 *
 * This used to be two columns — five slots left, four right — flexed beside a
 * portrait. Nothing lined up: the columns were centred against each other, so
 * the right-hand slots sat half a row lower than the left, the portrait
 * floated in the middle at a third height, and the whole thing read as a list,
 * a picture and another list rather than as a figure.
 *
 * Four and four now flank a portrait that spans all four rows, and the weapon
 * hangs beneath it in the centre. Armour reads down the left, carried things
 * down the right, and the odd ninth slot is the one every player looks for
 * first.
 */
export const LEFT_SLOTS: readonly DollSlot[] = [
  { key: 'head', label: GEAR_SLOT_LABEL.HEAD, gearSlot: 'HEAD' },
  { key: 'chest', label: GEAR_SLOT_LABEL.CHEST, gearSlot: 'CHEST' },
  { key: 'hands', label: GEAR_SLOT_LABEL.HANDS, gearSlot: 'HANDS' },
  { key: 'feet', label: GEAR_SLOT_LABEL.FEET, gearSlot: 'FEET' },
];

export const RIGHT_SLOTS: readonly DollSlot[] = [
  { key: 'back', label: GEAR_SLOT_LABEL.BACK, gearSlot: 'BACK' },
  { key: 'waist', label: GEAR_SLOT_LABEL.WAIST, gearSlot: 'WAIST' },
  { key: 'trinket', label: GEAR_SLOT_LABEL.TRINKET, gearSlot: 'TRINKET' },
  { key: 'offhand', label: NC_('gear slot', 'Off-hand'), slot: 'OFFHAND' },
];

/** Under the figure, where a drawn sword hangs. */
export const CENTRE_SLOTS: readonly DollSlot[] = [
  { key: 'weapon', label: NC_('gear slot', 'Weapon'), slot: 'WEAPON' },
];

/** Every slot the doll draws, for callers that do not care where it sits. */
export const ALL_DOLL_SLOTS: readonly DollSlot[] = [...LEFT_SLOTS, ...CENTRE_SLOTS, ...RIGHT_SLOTS];

const PLATE = 'rounded-md border border-[#57462f] '
  + 'bg-[radial-gradient(circle_at_50%_26%,#5b4a37,#2b2219_74%)] shadow-[inset_0_1px_0_rgba(255,222,176,0.10)]';

/**
 * A worn-gear slot: the piece in it, or an empty socket you can fill.
 *
 * Empty renders as "Empty" rather than as the old crossed-out plate, because
 * the fact it communicates has changed. A cross meant "nothing exists that
 * goes here"; now something does, and the honest word for a slot with nothing
 * in it is empty.
 */
function wornSlotMarkup(slot: DollSlot, item: GearCatalogRow | null): string {
  const label = tc('gear slot', slot.label);
  const name = item ? t(item.name) : '';
  const inner = item
    ? `<img ${ICON_FALLBACK_ATTR} src="${esc(wornIconSrc(item.id))}" alt="${esc(name)}" loading="lazy"
            class="absolute inset-0 w-full h-full object-contain p-1" />`
    : `<span class="text-[9px] leading-tight text-text-dim/70 px-0.5 text-center">${esc(t('Empty'))}</span>`;
  const title = item
    // Worn rows can arrive without a tier name; then the tooltip is name and weight only.
    ? item.tierName
      ? t('{name} — {tier}, {kg}kg', { name, tier: t(item.tierName), kg: formatNumber(item.weightKg) })
      : `${name} — ${t('{kg}kg', { kg: formatNumber(item.weightKg) })}`
    : t('{slot} — empty', { slot: label });
  const frame = item ? `${PLATE} ${tierFrameClass(item.tier)}` : 'frame-empty';
  return `
    <button data-doll-gear="${esc(slot.gearSlot ?? '')}"
      title="${esc(title)}" aria-label="${esc(t('{slot}: {item}', { slot: label, item: item ? name : t('empty') }))}"
      class="flex flex-col items-center gap-1 min-h-11">
      <span class="relative block w-12 h-12 grid place-items-center ${frame} hover:border-gold/70">${inner}</span>
      <span class="font-heading font-semibold text-[12px] ${item ? 'text-[#c2a06a]' : 'text-text-dim/70'}">${esc(label)}</span>
    </button>
  `;
}

/** A live slot: the thing in it, or an empty hand you can fill. */
function liveSlotMarkup(slot: DollSlot, item: EquipmentCatalogRow | null, locked: boolean): string {
  const label = tc('gear slot', slot.label);
  const name = item ? t(item.name) : '';
  const inner = item
    ? `<img ${ICON_FALLBACK_ATTR} src="${esc(gearIconSrc(item.model))}" alt="${esc(name)}" loading="lazy"
            class="absolute inset-0 w-full h-full object-contain p-1" />`
    : `<span class="text-[9px] leading-tight text-text-dim/70 px-0.5 text-center">${esc(locked ? t('Both hands') : t('Empty'))}</span>`;
  const title = item
    ? t('{name} — {blurb}', { name, blurb: t(item.blurb) })
    : locked ? t('Your weapon needs both hands') : t('Empty hand — 0kg');
  const frame = item ? PLATE : 'frame-empty';
  const ring = locked ? 'opacity-55 cursor-not-allowed' : 'hover:border-gold/70';
  return `
    <button data-doll-slot="${esc(slot.slot ?? '')}" ${locked ? 'disabled aria-disabled="true"' : ''}
      title="${esc(title)}" aria-label="${esc(t('{slot}: {item}', { slot: label, item: item ? name : t('empty') }))}"
      class="flex flex-col items-center gap-1 min-h-11 disabled:cursor-not-allowed">
      <span class="relative block w-12 h-12 grid place-items-center ${frame} ${ring}">${inner}</span>
      <span class="font-heading font-semibold text-[12px] ${item ? 'text-[#c2a06a]' : 'text-text-dim/70'}">${esc(label)}</span>
    </button>
  `;
}

function slotCellMarkup(
  slot: DollSlot,
  arms: EquippedArms | null,
  twoHanded: boolean,
  worn: Partial<Record<GearSlot, GearCatalogRow | null>>,
): string {
  if (slot.gearSlot) return wornSlotMarkup(slot, worn[slot.gearSlot] ?? null);
  if (slot.slot === 'WEAPON') return liveSlotMarkup(slot, arms?.weapon ?? null, false);
  return liveSlotMarkup(slot, arms?.offhand ?? null, twoHanded);
}

function satchelRowMarkup(loadout: LoadoutRow | null): string {
  const slots = loadout?.satchel ?? [];
  const cells = slots.length > 0
    ? slots.map((s) => `
        <span class="relative block w-11 h-11 ${PLATE}" title="${esc(tp(s.charges, '{name} — {n} charge', '{name} — {n} charges', { name: t(s.name) }))}">
          <img ${ICON_FALLBACK_ATTR} src="${esc(consumableIconSrc(s.defId))}" alt="${esc(t(s.name))}" loading="lazy"
               class="absolute inset-0 w-full h-full object-contain p-1" />
          <span class="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full
                       border border-[#2b2219] bg-[#d8ab5c] text-[#241a10] text-[10px] font-bold tabular-nums">${s.charges}</span>
        </span>`)
    : [`<span class="frame-empty block w-11 h-11 grid place-items-center
                     text-[9px] text-text-dim/70" title="${esc(t('Buy charges in the Shop'))}">${esc(t('Empty'))}</span>`];
  // Label under the slots, because every other slot on this doll labels
  // itself that way and one row reading bottom-up made the whole column look
  // like it had lost its alignment.
  return `
    <div class="flex flex-col items-center gap-1">
      <div class="flex gap-1.5">${cells.join('')}</div>
      <span class="font-heading font-semibold text-[12px] text-text-dim/70">${esc(t('Satchel'))}</span>
    </div>
  `;
}

/**
 * The armour line.
 *
 * Read-only and stated as the trade it is: PLATE turns aside 30% of physical
 * damage and none of the magic, CLOTH is the exact inverse.
 *
 * It no longer says "(set by class)". That was true until the gear ladder
 * shipped and is now the opposite of true — these are the *resolved* numbers,
 * class base plus everything worn, and a player who put a cuirass on and saw
 * the line still credit their class would reasonably conclude it had not
 * worked. The suffix now names what is actually moving them.
 */
/** The armour class word, translated; the server sends the enum. */
export function armourClassLabel(armorClass: string): string {
  switch (armorClass) {
    case 'CLOTH': return t('CLOTH');
    case 'LEATHER': return t('LEATHER');
    case 'MAIL': return t('MAIL');
    case 'PLATE': return t('PLATE');
    default: return armorClass;
  }
}

export function armourLineMarkup(loadout: LoadoutRow | null): string {
  if (!loadout) return '';
  const g = loadout.gear;
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const speed = Math.round((1 - g.moveSpeedMultiplier) * 100);
  const wornCount = Object.keys(g.worn ?? {}).length;
  const source = wornCount > 0
    ? tp(wornCount, 'class + {n} worn piece', 'class + {n} worn pieces')
    : t('set by class');
  // Three stacked lines rather than one sentence with middots in it
  // (2026-09-06). In a 260px column the single line broke as "... 0% magic ·
  // / 20% slower (set by class)" — a wrap *inside* the list, so the reader's
  // eye has to reassemble which number goes with which word. A wrapping flex
  // row can only break between facts, never inside one.
  const facts = [
    t('{pct} physical', { pct: pct(g.physicalReduction) }),
    t('{pct} magic', { pct: pct(g.magicResistance) }),
    // Passive dodge (2026-10-16) — class base + gear + talents, unladen.
    ...(g.dodgeChance ? [t('{pct} dodge', { pct: `${formatNumber(Math.round(g.dodgeChance * 1000) / 10)}%` })] : []),
    ...(speed > 0 ? [t('{n}% slower', { n: speed })] : []),
  ];
  return `
    <div class="text-[11px] text-text-dim text-center leading-snug">
      <p>${t('{class} armour', { class: `<span class="text-[#c2a06a]">${esc(armourClassLabel(g.armorClass))}</span>` })}</p>
      <p class="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
        ${facts.map((f) => `<span class="whitespace-nowrap">${esc(f)}</span>`).join('<span class="opacity-40">&middot;</span>')}
      </p>
      <p class="opacity-60">${esc(source)}</p>
    </div>
  `;
}

export interface PaperdollView {
  classId: CharacterClassId;
  characterName: string;
  arms: EquippedArms | null;
  loadout: LoadoutRow | null;
  /** equipment.md's worn-gear amendment — what is in the four gear slots. */
  worn?: Partial<Record<GearSlot, GearCatalogRow | null>>;
}

export function paperdollMarkup(view: PaperdollView): string {
  const twoHanded = (view.arms?.weapon.hands ?? 1) === 2;
  const worn = view.worn ?? {};
  const cell = (slot: DollSlot): string => slotCellMarkup(slot, view.arms, twoHanded, worn);
  // One grid rather than three stacks, so row 2 on the left is row 2 on the
  // right and the portrait is as tall as the body it is standing in for.
  //
  // Four rows, and the centre column is the figure standing over its own
  // weapon: portrait across rows 1-3, the weapon hand in row 4 where a drawn
  // sword hangs.
  //
  // The portrait cell carries `w-full` because the grid centres its items and
  // a div with no intrinsic width centres to nothing — the first attempt drew
  // it as a one-pixel hairline between the columns. The image is contained
  // rather than cover-cropped: a niche this narrow zooms a cover-cropped
  // portrait down to one eye and a nose.
  const rows = LEFT_SLOTS.map((left, i) => {
    const right = RIGHT_SLOTS[i];
    const centre = i === 0
      ? `<div class="row-span-3 w-full self-stretch justify-self-stretch frame-inset relative overflow-hidden min-h-[168px]
                    bg-[radial-gradient(circle_at_50%_30%,#4a3b2c,#1c1610_78%)]">
           <!--
             A pool of light at the feet, so the delver stands on the plate
             instead of hovering in it, sat where object-bottom puts the boots.
           -->
           <span class="absolute inset-x-3 bottom-2 h-3 rounded-[50%] bg-[#0b0805]/55 blur-[3px]"></span>
           <img src="/assets/ui/paperdoll/${esc(view.classId)}.webp" alt="${esc(view.characterName)}"
                class="absolute inset-0 w-full h-full object-contain object-bottom p-1 pb-1.5 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]" />
         </div>`
      : i === LEFT_SLOTS.length - 1 ? cell(CENTRE_SLOTS[0]) : '';
    return `${cell(left)}${centre}${right ? cell(right) : '<span></span>'}`;
  }).join('');
  return `
    <div class="flex flex-col gap-3">
      <div class="grid grid-cols-[auto_minmax(104px,1fr)_auto] gap-x-2 gap-y-2 justify-items-center items-start">
        ${rows}
      </div>
      ${satchelRowMarkup(view.loadout)}
      ${armourLineMarkup(view.loadout)}
    </div>
  `;
}

/**
 * The picker that opens when a live slot is tapped.
 *
 * Every option carries the numbers behind it, because the whole point of
 * making these slots real is that the choice has consequences: a greatsword
 * hits for 52 and costs 25 stamina a swing against a dagger's 18 and 12.
 */
export function pickerMarkup(slot: EquipmentSlot, options: readonly EquipmentCatalogRow[], equippedId: string | null): string {
  // Owned only, since 2026-09-06. User: *"from inventory you can look and equip
  // only what you own. All other in shop, you can buy and equip."*
  //
  // The picker used to double as a storefront — a locked row with a price on
  // it, buying from inside the inventory. Two places to buy the same sword is
  // one more than the number of places a player should have to check, and the
  // Shop is the one that reads as a shop. What is left here is a wardrobe.
  const owned = options.filter((option) => option.owned);
  const cards = owned.map((option) => {
    const on = option.id === equippedId;
    const stats = option.slot === 'WEAPON'
      ? esc(t('{dmg} dmg · {swing}s · {reach}m · {sta} sta · {kg}kg', {
        dmg: option.damage ?? 0, swing: formatNumber((option.swingMs ?? 0) / 1000, 2), reach: formatNumber(option.reachM ?? 0),
        sta: option.staminaCost ?? 0, kg: formatNumber(option.totalWeightKg),
      }))
      : esc(t('{kg}kg', { kg: formatNumber(option.totalWeightKg) }));
    const hands = option.hands === 2 ? ` &middot; ${esc(t('two-handed'))}` : '';
    // A locked piece buys, an owned one equips. Both are the same card, so
    // the stat line sits in the same place either way — which is the point:
    // a paid piece's numbers are identical to a free one's, and putting them
    // side by side is how a player sees that rather than being told it.
    //
    // Dimmed but never desaturated. The first pass greyscaled locked icons
    // and the pale steel ones came out as near-white blobs — which is the
    // wrong thing to hide, because the look is the entire product here. The
    // gold price line carries "locked" on its own.
    const locked = false; // nothing unowned reaches this list any more
    const action = 'data-equip-choice';
    const priceLine = locked && option.priceWax !== undefined
      ? `<span class="block text-[11px] text-gold">${amountMarkup('wax', option.priceWax, { hideLabel: true })} &middot; ${esc(t('look only, same numbers'))}</span>`
      : '';
    // The detail button is a sibling, not a child — a button inside a button
    // is invalid markup and browsers resolve it by dropping one of them. It is
    // pinned to the top-right corner so adding it moved no existing pixel.
    return `
      <div class="relative">
      <button ${action}="${esc(option.id)}" aria-pressed="${on}"
        class="btn ${on ? 'btn-stone-on' : 'btn-stone'} w-full justify-start gap-3 p-2 pr-11 h-auto text-left">
        <span class="relative block w-12 h-12 shrink-0 ${PLATE}">
          <img ${ICON_FALLBACK_ATTR} src="${esc(gearIconSrc(option.model))}" alt="" loading="lazy"
               class="absolute inset-0 w-full h-full object-contain p-1 ${locked ? 'opacity-75' : ''}" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-[13px] truncate ${on ? 'text-gold' : ''}">${esc(t(option.name))}</span>
          <span class="block text-[11px] text-text-dim truncate">${stats}${hands}</span>
          <span class="block text-[11px] text-text-dim/75 truncate">${esc(t(option.blurb))}</span>
          ${priceLine}
        </span>
      </button>
      <button data-detail-arms="${esc(option.id)}" aria-label="${esc(t('What is {name}?', { name: t(option.name) }))}"
        class="absolute top-1 right-1 w-9 h-9 grid place-items-center rounded-full
               text-[13px] text-text-dim border border-border/70 hover:text-gold hover:border-gold/70">i</button>
      </div>
    `;
  });

  // Only the off-hand can be emptied — you always hold a weapon.
  const empty = slot === 'OFFHAND'
    ? `
      <button data-equip-choice="" aria-pressed="${equippedId === null}"
        class="btn ${equippedId === null ? 'btn-stone-on' : 'btn-stone'} w-full justify-start gap-3 p-2 h-auto text-left">
        <span class="frame-empty block w-12 h-12 shrink-0"></span>
        <span class="min-w-0 flex-1">
          <span class="block text-[13px] ${equippedId === null ? 'text-gold' : ''}">${esc(t('Empty hand'))}</span>
          <span class="block text-[11px] text-text-dim">${esc(t('{kg}kg', { kg: 0 }))}</span>
          <span class="block text-[11px] text-text-dim/75">${esc(t('Carry nothing, and carry more out.'))}</span>
        </span>
      </button>`
    : '';

  return `<div class="grid gap-2 sm:grid-cols-2">${cards.join('')}${empty}</div>`;
}

/**
 * How a stat reads on a card.
 *
 * Signed, always — "+4 armour" and "-2% speed" are the same kind of fact and a
 * Ward piece is defined by having both. A bare "2% speed" on a piece that
 * *costs* you speed is the single most misleading thing this picker could print.
 */
export function statLines(stats: GearCatalogRow['stats']): string[] {
  const lines: string[] = [];
  const pct = (v: number): string => `${v > 0 ? '+' : ''}${Math.round(v * 100)}`;
  const signed = (v: number): string => `${v > 0 ? '+' : ''}${v}`;
  if (stats.physicalReduction) lines.push(t('{n} armour', { n: pct(stats.physicalReduction) }));
  if (stats.magicResistance) lines.push(t('{n} magic resist', { n: pct(stats.magicResistance) }));
  if (stats.hpBonus) lines.push(t('{n} health', { n: signed(stats.hpBonus) }));
  if (stats.staminaBonus) lines.push(t('{n} stamina', { n: signed(stats.staminaBonus) }));
  if (stats.poise) lines.push(t('{n} poise', { n: signed(stats.poise) }));
  if (stats.carryKg) lines.push(t('{n}kg carry', { n: signed(stats.carryKg) }));
  if (stats.moveSpeedDelta) lines.push(t('{n}% speed', { n: pct(stats.moveSpeedDelta) }));
  // Passive dodge (2026-10-16). Half-points exist (0.015), so one decimal.
  if (stats.dodgeChance) {
    const points = Math.round(stats.dodgeChance * 1000) / 10;
    lines.push(t('{n}% dodge', { n: `${points > 0 ? '+' : ''}${formatNumber(points)}` }));
  }
  return lines;
}

/**
 * What the set adds up to, above the options.
 *
 * The totals matter more than any one card here: two pieces that each cost 1%
 * of your speed are a 2% loss you will feel, and nothing else on this screen
 * would tell you that.
 */
export function wornTotalsMarkup(bonuses: GearBonuses): string {
  const parts = statLines({
    physicalReduction: bonuses.physicalReduction,
    magicResistance: bonuses.magicResistance,
    hpBonus: bonuses.hpBonus,
    staminaBonus: bonuses.staminaBonus,
    poise: bonuses.poise,
    carryKg: bonuses.carryKg,
    moveSpeedDelta: bonuses.moveSpeedDelta,
    dodgeChance: bonuses.dodgeChance,
  });
  if (parts.length === 0) {
    return `<p class="text-[11px] text-text-dim">${esc(t('Nothing worn. Every slot is empty.'))}</p>`;
  }
  return `
    <p class="text-[11px] text-text-dim">
      ${t('Worn set: {stats}', { stats: `<span class="text-[#c2a06a]">${esc(parts.join(' · '))}</span>` })}
      &middot; ${esc(t('{kg}kg', { kg: formatNumber(bonuses.weightKg) }))}
    </p>`;
}

/**
 * The picker for one worn slot.
 *
 * Unowned pieces are listed rather than hidden. A ladder you cannot see the
 * rungs of is not a ladder — the whole reason to go one floor deeper is that
 * you know what is down there — so a piece you have not found yet shows its
 * numbers and says which floor drops it. Tier 1 additionally shows a price,
 * because that shelf is the one thing here you can simply buy.
 */
export function gearPickerMarkup(
  options: readonly GearCatalogRow[],
  equippedId: string | null,
  bonuses: GearBonuses,
): string {
  // Owned only (2026-09-06) — the wardrobe, not the storefront. Buying
  // moved wholly to the Shop, which sells and equips in one place.
  const cards = options.filter((option) => option.owned).map((option) => {
    const on = option.id === equippedId;
    const stats = statLines(option.stats).join(' · ');
    const locked = false; // owned only — see the filter above
    const action = 'data-equip-gear';
    const source = '';
    return `
      <button ${action}="${esc(option.id)}" aria-pressed="${on}"
        class="btn ${on ? 'btn-stone-on' : 'btn-stone'} w-full justify-start gap-3 p-2 h-auto text-left disabled:opacity-100">
        <span class="relative block w-12 h-12 shrink-0 ${PLATE} ${tierFrameClass(option.tier)}">
          <img ${ICON_FALLBACK_ATTR} src="${esc(wornIconSrc(option.id))}" alt="" loading="lazy"
               class="absolute inset-0 w-full h-full object-contain p-1 ${locked ? 'opacity-60' : ''}" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-[13px] truncate ${on ? 'text-gold' : ''}">${esc(t(option.name))}</span>
          <span class="block text-[11px] text-text-dim truncate">${esc(stats)}</span>
          <span class="block text-[11px] text-text-dim/75 truncate">${esc(option.line === 'WARD' ? t('Ward') : t('Weave'))} &middot; ${esc(t(option.tierName))} &middot; ${esc(t('{kg}kg', { kg: formatNumber(option.weightKg) }))}</span>
          ${source}
        </span>
      </button>
    `;
  });

  // Every gear slot may be emptied — unlike the weapon hand, which always
  // holds something. Emptying is a real choice on a Ward piece: the weight
  // comes straight back as carry capacity.
  const empty = `
    <button data-equip-gear="" aria-pressed="${equippedId === null}"
      class="btn ${equippedId === null ? 'btn-stone-on' : 'btn-stone'} w-full justify-start gap-3 p-2 h-auto text-left">
      <span class="frame-empty block w-12 h-12 shrink-0"></span>
      <span class="min-w-0 flex-1">
        <span class="block text-[13px] ${equippedId === null ? 'text-gold' : ''}">${esc(t('Nothing'))}</span>
        <span class="block text-[11px] text-text-dim">${esc(t('{kg}kg', { kg: 0 }))}</span>
        <span class="block text-[11px] text-text-dim/75">${esc(t('Carry nothing here, and carry more out.'))}</span>
      </span>
    </button>`;

  return `
    <div class="mb-2">${wornTotalsMarkup(bonuses)}</div>
    <div class="grid gap-2 sm:grid-cols-2">${cards.join('')}${empty}</div>`;
}
